import React, { useState } from "react";
import { OverallProgressCard } from "./OverallProgressCard";
import { EarnedXPCard } from "./EarnedXPCard";
import { LearningPathFeed } from "./LearningPathFeed";
import { CourseSequenceView } from "./CourseSequenceView";
import { CourseFeedbackDialog } from "./CourseFeedbackDialog";
import { Button } from "@/components/ui/button";
import { MessageCircleHeart } from "lucide-react";
import { cn } from "@/lib/utils";
import type { CourseNodeWithStatus, DailyXp } from "@/types/courses";

interface CourseContentProps {
  nodes: CourseNodeWithStatus[];
  courseName: string;
  courseId: string;
  progress: number;
  estimatedCompletion: string;
  totalXp: number;
  todayXp: number;
  dailyTarget: number;
  weeklyXp: DailyXp[];
  contentStepCounts: Map<string, number>;
  onNodeAction: (nodeId: string) => void;
  onOpenGraph?: () => void;
}

export const CourseContent: React.FC<CourseContentProps> = ({
  nodes,
  courseName,
  courseId,
  progress,
  estimatedCompletion,
  totalXp,
  todayXp,
  dailyTarget,
  weeklyXp,
  contentStepCounts,
  onNodeAction,
  onOpenGraph,
}) => {
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [viewMode, setViewMode] = useState<"path" | "sequence">("path");

  return (
    <div className="px-4 sm:px-6 pb-8">
      {/* Toolbar - spans full width, right-aligned */}
      <div className="flex items-center justify-end gap-2 mb-4">
        <Button variant="outline" size="sm" className="gap-2 text-sm font-medium" onClick={() => setFeedbackOpen(true)}>
          <MessageCircleHeart className="h-4 w-4" />
          Give Feedback
        </Button>
        <div className="flex items-center gap-0 rounded-lg border border-border bg-background p-0.5">
          <button
            onClick={() => setViewMode("path")}
            className={cn(
              "text-sm font-medium px-3 py-1 rounded-md transition-colors",
              viewMode === "path"
                ? "text-foreground bg-muted"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            Path
          </button>
          <button
            onClick={() => setViewMode("sequence")}
            className={cn(
              "text-sm font-medium px-3 py-1 rounded-md transition-colors",
              viewMode === "sequence"
                ? "text-foreground bg-muted"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            Sequence
          </button>
        </div>
      </div>

      {viewMode === "path" ? (
        /* Path: Two-column layout */
        <div className="flex flex-col lg:flex-row gap-6">
          <div className="w-full lg:w-[380px] xl:w-[420px] flex-shrink-0 space-y-4">
            <OverallProgressCard
              progress={progress}
              estimatedCompletion={estimatedCompletion}
              onOpenGraph={onOpenGraph}
            />
            <EarnedXPCard
              totalXp={totalXp}
              todayXp={todayXp}
              dailyTarget={dailyTarget}
              weeklyXp={weeklyXp}
            />
          </div>
          <div className="flex-1 min-w-0">
            <LearningPathFeed
              nodes={nodes}
              courseName={courseName}
              onNodeAction={onNodeAction}
            />
          </div>
        </div>
      ) : (
        /* Sequence: Full-width syllabus */
        <CourseSequenceView
          nodes={nodes}
          courseName={courseName}
          contentStepCounts={contentStepCounts}
          onNodeAction={onNodeAction}
        />
      )}

      <CourseFeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} courseId={courseId} />
    </div>
  );
};
